// SeguroCor (SC-232): templates padrão do sistema e a experiência de preencher variáveis.
// O Hub cria em toda corretora um conjunto de templates com o prefixo `padrao_` e variáveis
// nomeadas ({{nome}}, {{assunto}}...). Aqui ficam o selo, os títulos amigáveis, os rótulos de cada
// variável e o pré-preenchimento com o nome do contato e do atendente. Puro: sem store nem i18n.

export const SYSTEM_TEMPLATE_PREFIX = 'padrao_';

// Títulos dos templates padrão (o nome técnico na Meta não muda; o título é o que o atendente lê).
const SYSTEM_TEMPLATE_TITLES = {
  padrao_retorno_contato: 'Retomar contato',
  padrao_resposta_solicitacao: 'Responder mensagem recebida',
  padrao_atualizacao_atendimento: 'Dar uma atualização',
  padrao_documentos_pendentes: 'Pedir documentos',
  padrao_vencimento_seguro: 'Avisar vencimento do seguro',
  padrao_lembrete_parcela: 'Lembrar parcela',
  padrao_confirmacao_horario: 'Confirmar horário',
};

export const PREFILL = { CONTACT_FIRST_NAME: 'contact', AGENT_NAME: 'agent' };

// Variáveis conhecidas: rótulo, exemplo e de onde vem o valor inicial. Vale para qualquer template
// com variável nomeada, inclusive os que a corretora criou com os mesmos nomes.
const VARIABLES = {
  nome: {
    label: 'Nome do cliente',
    hint: 'Maria',
    prefill: PREFILL.CONTACT_FIRST_NAME,
  },
  nome_cliente: {
    label: 'Nome do cliente',
    hint: 'Maria',
    prefill: PREFILL.CONTACT_FIRST_NAME,
  },
  cliente: {
    label: 'Nome do cliente',
    hint: 'Maria',
    prefill: PREFILL.CONTACT_FIRST_NAME,
  },
  atendente: { label: 'Seu nome', hint: 'Ana', prefill: PREFILL.AGENT_NAME },
  nome_atendente: {
    label: 'Seu nome',
    hint: 'Ana',
    prefill: PREFILL.AGENT_NAME,
  },
  assunto: { label: 'Assunto', hint: 'a renovação do seu seguro auto' },
  atualizacao: {
    label: 'O que mudou',
    hint: 'a vistoria foi aprovada e o reparo foi liberado',
  },
  documentos: {
    label: 'Documentos que faltam',
    hint: 'foto da CNH e do documento do veículo',
  },
  seguro: { label: 'Seguro', hint: 'auto (placa ABC1D23)' },
  data_vencimento: { label: 'Data de vencimento', hint: '15/11/2026' },
  valor: { label: 'Valor', hint: 'R$ 89,90' },
  data_hora: { label: 'Data e horário', hint: '14/10 às 10h' },
};

export const isSystemTemplate = template =>
  String(template?.name || '').startsWith(SYSTEM_TEMPLATE_PREFIX);

const capitalize = text =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

export const humanize = name =>
  capitalize(
    String(name || '')
      .replace(/_+/g, ' ')
      .trim()
  );

/** Título que o atendente vê: o amigável dos padrão; nos demais, o nome sem underscores. */
export const templateTitle = template =>
  SYSTEM_TEMPLATE_TITLES[template?.name] || humanize(template?.name);

const CATEGORY_LABELS = {
  UTILITY: 'Utilidade',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticação',
};

export const categoryLabel = category =>
  CATEGORY_LABELS[String(category || '').toUpperCase()] || category || '';

/** Rótulo e exemplo de uma variável do corpo. Numerada ({{1}}) vira "Variável 1". */
export const variableMeta = key => {
  const known = VARIABLES[key];
  if (known) return known;
  if (/^\d+$/.test(String(key))) return { label: `Variável ${key}`, hint: '' };
  return { label: humanize(key), hint: '' };
};

const looksLikePhoneOrEmpty = text =>
  !text || /^[\d\s()+\-.]+$/.test(text) || text.includes('@');

const tidyCase = word =>
  word === word.toUpperCase() || word === word.toLowerCase()
    ? capitalize(word.toLowerCase())
    : word;

/** Primeiro nome legível do contato. Telefone, e-mail ou vazio não viram nome. */
export const firstName = fullName => {
  const text = String(fullName || '').trim();
  if (looksLikePhoneOrEmpty(text)) return '';
  return tidyCase(text.split(/\s+/)[0]);
};

const prefillValue = (kind, { contact, agent }) => {
  if (kind === PREFILL.CONTACT_FIRST_NAME) return firstName(contact?.name);
  if (kind === PREFILL.AGENT_NAME) {
    return firstName(
      agent?.available_name || agent?.display_name || agent?.name
    );
  }
  return '';
};

/**
 * Preenche as variáveis do corpo que têm origem conhecida (nome do contato, nome do atendente).
 * Não sobrescreve o que já foi digitado e devolve um objeto novo.
 */
export const prefillTemplateParams = (processedParams, sources = {}) => {
  const body = processedParams?.body;
  if (!body) return processedParams;
  const filled = Object.fromEntries(
    Object.entries(body).map(([key, value]) => {
      if (value) return [key, value];
      const kind = VARIABLES[key]?.prefill;
      return [key, kind ? prefillValue(kind, sources) : value];
    })
  );
  return { ...processedParams, body: filled };
};

/** Padrão do sistema primeiro; dentro de cada grupo, por título. */
export const sortTemplates = templates =>
  [...(templates || [])].sort((a, b) => {
    const system = Number(isSystemTemplate(b)) - Number(isSystemTemplate(a));
    if (system !== 0) return system;
    return templateTitle(a).localeCompare(templateTitle(b), 'pt-BR');
  });

const normalize = text =>
  String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const bodyText = template =>
  template?.components?.find(c => c.type === 'BODY')?.text || '';

/** Busca pelo título, pelo nome técnico e pelo texto, sem diferenciar acento. */
export const templateMatches = (template, query) => {
  const q = normalize(query).trim();
  if (!q) return true;
  return [templateTitle(template), template?.name, bodyText(template)].some(
    field => normalize(field).includes(q)
  );
};

/**
 * Corpo quebrado em pedaços de texto e de variável, para o preview destacar cada valor no lugar.
 * Variável vazia volta com `value: ''` (o preview mostra o rótulo no lugar).
 */
export const templateSegments = (text, values = {}) => {
  const segments = [];
  const regex = /{{([^}]+)}}/g;
  let last = 0;
  let match = regex.exec(text || '');
  while (match) {
    if (match.index > last) {
      segments.push({ type: 'text', text: text.slice(last, match.index) });
    }
    const key = match[1].trim();
    segments.push({ type: 'variable', key, value: values[key] || '' });
    last = match.index + match[0].length;
    match = regex.exec(text);
  }
  if (last < (text || '').length) {
    segments.push({ type: 'text', text: text.slice(last) });
  }
  return segments;
};
